import React, { useState, useRef, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import { ExpandMore } from "@material-ui/icons";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";

import alignmentSessionStyles from "./styles";
import { getValueColor, getElementHighlightStyles } from "./utils";

// Format large numbers with magnitude suffix (k, M, B)
// Format percentage values - removes decimal for |value| >= 100
const formatPercentValue = (value, showSign = true) => {
  const absValue = Math.abs(value);
  const sign = showSign ? (value >= 0 ? "+" : "") : "";
  return absValue >= 100
    ? `${sign}${value.toFixed(0)}`
    : `${sign}${value.toFixed(1)}`;
};

// Format large numbers with magnitude suffix (k, M, B)
// Removes decimal for values >= 100 in the magnitude unit
const formatMarginValue = (value) => {
  const absValue = Math.abs(value);
  const sign = value >= 0 ? "+" : "";

  if (absValue >= 1000000000) {
    const scaled = value / 1000000000;
    return `${sign}${Math.abs(scaled) >= 100 ? scaled.toFixed(0) : scaled.toFixed(1)} B`;
  } else if (absValue >= 1000000) {
    const scaled = value / 1000000;
    return `${sign}${Math.abs(scaled) >= 100 ? scaled.toFixed(0) : scaled.toFixed(1)} M`;
  } else if (absValue >= 1000) {
    const scaled = value / 1000;
    return `${sign}${Math.abs(scaled) >= 100 ? scaled.toFixed(0) : scaled.toFixed(1)} k`;
  }
  return `${sign}${value.toFixed(0)}`;
};

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ChartDataLabels,
);

const useStyles = makeStyles(alignmentSessionStyles);

/**
 * DimensionTable component
 * Displays a table of dimension elements with delta metrics and criticality indicators
 * Includes a mini heatmap at the bottom showing margin by month/year
 */
function DimensionTable({
  dimensionKey,
  dimensionData,
  totalData,
  isTotal = false,
  onDimensionChange,
  highlightEnabled = false,
  dimensionOptions: propDimensionOptions,
}) {
  const classes = useStyles();
  const { t } = useTranslation();

  // Dropdown state
  const [dimensionMenuOpen, setDimensionMenuOpen] = useState(false);
  const dimensionMenuRef = useRef(null);

  // Dimension options: use prop if provided, otherwise fallback to hardcoded
  const dimensionOptions = useMemo(
    () =>
      propDimensionOptions || [
        {
          value: "channel",
          label: t("alignment_session.by_dimension.dimensions.channel"),
        },
        {
          value: "category",
          label: t("alignment_session.by_dimension.dimensions.category"),
        },
        {
          value: "region",
          label: t("alignment_session.by_dimension.dimensions.region"),
        },
      ],
    [propDimensionOptions, t],
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dimensionMenuRef.current &&
        !dimensionMenuRef.current.contains(event.target)
      ) {
        setDimensionMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const rawData = dimensionData[dimensionKey];
  const totalElement = totalData.elements[0];

  // Sort elements by share (representation) from largest to smallest
  const sortedElements = useMemo(() => {
    if (!rawData?.elements) return [];
    return [...rawData.elements].sort((a, b) => b.share - a.share);
  }, [rawData]);

  // Create sorted data object
  const data = useMemo(
    () => ({
      ...rawData,
      elements: sortedElements,
    }),
    [rawData, sortedElements],
  );

  // Get current dimension label
  const currentDimensionLabel =
    dimensionOptions.find((d) => d.value === dimensionKey)?.label ||
    data?.name ||
    "";

  // Handle dimension change
  const handleDimensionSelect = (dimension) => {
    setDimensionMenuOpen(false);
    if (onDimensionChange && dimension !== dimensionKey) {
      onDimensionChange(dimension);
    }
  };

  // Generate color palette for stacked bar chart
  const chartColors = useMemo(() => {
    const colors = [
      "#1976d2", // blue
      "#66BB6A", // green
      "#FFA726", // orange
      "#EF5350", // red
      "#AB47BC", // purple
      "#26C6DA", // cyan
      "#8D6E63", // brown
      "#78909C", // blue grey
      "#7E57C2", // deep purple
      "#26A69A", // teal
      "#D4E157", // lime
      "#FF7043", // deep orange
    ];
    return colors;
  }, []);

  // Prepare chart data for stacked bar
  const chartData = useMemo(() => {
    if (!data?.elements) return null;

    const elements = data.elements;
    const datasets = elements.map((element, idx) => ({
      label: element.name,
      data: [element.sharePrevious || 0, element.share || 0],
      backgroundColor: chartColors[idx % chartColors.length],
      borderWidth: 0,
    }));

    return {
      // Use explicit period labels (will be dynamic with API integration)
      labels: ["Acum. 2024", "Acum. 2025"],
      datasets,
    };
  }, [data, chartColors]);

  // Chart options
  const chartOptions = useMemo(
    () => ({
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              return `${context.dataset.label}: ${context.raw.toFixed(1)}%`;
            },
          },
        },
        datalabels: {
          display: (context) => {
            // Only show label if value >= 5%
            return context.dataset.data[context.dataIndex] >= 5;
          },
          color: "#fff",
          font: {
            family: '"Roboto", "Helvetica", "Arial", sans-serif',
            size: 12,
            weight: "500",
          },
          formatter: (value) => {
            return `${value.toFixed(0)}%`;
          },
          anchor: "center",
          align: "center",
        },
      },
      scales: {
        x: {
          stacked: true,
          max: 100,
          ticks: {
            callback: (value) => `${value}%`,
            font: {
              family: '"Roboto", "Helvetica", "Arial", sans-serif',
              size: 11,
              weight: "300",
            },
          },
          grid: {
            display: true,
            color: "#e0e0e0",
          },
        },
        y: {
          stacked: true,
          ticks: {
            font: {
              family: '"Roboto", "Helvetica", "Arial", sans-serif',
              size: 13,
              weight: "400",
            },
          },
          grid: {
            display: false,
          },
        },
      },
    }),
    [],
  );

  return (
    <div className={classes.dimensionTable}>
      {/* Upper Table with max height */}
      <div className={classes.dimensionTableWrapper}>
        {/* Table Header */}
        <div className={classes.dimensionTableHeader}>
          <div
            className={`${classes.dimensionTableHeaderCell} ${!isTotal ? classes.dimensionDropdownCell : ""}`}
            ref={dimensionMenuRef}
          >
            {isTotal ? (
              ""
            ) : (
              <>
                <div
                  className={classes.dimensionDropdownButton}
                  onClick={() => setDimensionMenuOpen(!dimensionMenuOpen)}
                >
                  <span>{currentDimensionLabel}</span>
                  <ExpandMore
                    className={`${classes.dimensionDropdownIcon} ${
                      dimensionMenuOpen ? classes.dimensionDropdownIconOpen : ""
                    }`}
                  />
                </div>
                {dimensionMenuOpen && (
                  <div className={classes.dimensionDropdownMenu}>
                    {dimensionOptions.map((option) => (
                      <div
                        key={option.value}
                        className={`${classes.dimensionDropdownItem} ${
                          dimensionKey === option.value
                            ? classes.dimensionDropdownItemActive
                            : ""
                        }`}
                        onClick={() => handleDimensionSelect(option.value)}
                      >
                        {option.label}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          <div className={classes.dimensionTableHeaderCell}>
            <span>{t("alignment_session.table_headers.delta_vol")}</span>
            <span className={classes.dimensionTableHeaderUnit}>%</span>
          </div>
          <div className={classes.dimensionTableHeaderCell}>
            <span>{t("alignment_session.table_headers.delta_price")}</span>
            <span className={classes.dimensionTableHeaderUnit}>%</span>
          </div>
          <div className={classes.dimensionTableHeaderCell}>
            <span>{t("alignment_session.table_headers.delta_revenue")}</span>
            <span className={classes.dimensionTableHeaderUnit}>%</span>
          </div>
          <div className={classes.dimensionTableHeaderCell}>
            <span>{t("alignment_session.table_headers.delta_margin")}</span>
            <span className={classes.dimensionTableHeaderUnit}>BRL</span>
          </div>
          <div className={classes.dimensionTableHeaderCell}>
            <span>{t("alignment_session.table_headers.delta_margin_pct")}</span>
            <span className={classes.dimensionTableHeaderUnit}>pp</span>
          </div>
        </div>

        {/* Table Body */}
        <div className={classes.dimensionTableBody}>
          {/* Total Companhia Row (first row) */}
          {!isTotal && (
            <div
              className={`${classes.dimensionTableRow} ${classes.dimensionTableRowTotal}`}
            >
              <div className={classes.dimensionTableCell}>
                <span className={classes.dimensionElementName}>
                  {t("alignment_session.by_dimension.total_company")}
                </span>
              </div>
              <div className={classes.dimensionTableCell}>
                <div className={classes.dimensionValueWithBadge}>
                  <span>{formatPercentValue(totalElement.deltaVolume)}</span>
                </div>
              </div>
              <div className={classes.dimensionTableCell}>
                <div className={classes.dimensionValueWithBadge}>
                  <span>
                    {formatPercentValue(totalElement.deltaAvgPrice || 0)}
                  </span>
                </div>
              </div>
              <div className={classes.dimensionTableCell}>
                <div className={classes.dimensionValueWithBadge}>
                  <span>{formatPercentValue(totalElement.deltaRevenue)}</span>
                </div>
              </div>
              <div className={classes.dimensionTableCell}>
                <div className={classes.dimensionValueWithBadge}>
                  <span>{formatMarginValue(totalElement.deltaMargin)}</span>
                </div>
              </div>
              <div className={classes.dimensionTableCell}>
                <div className={classes.dimensionValueWithBadge}>
                  <span>{formatPercentValue(totalElement.deltaMarginPct)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Dimension Elements */}
          {data.elements.map((element, idx) => {
            // Calculate colors using fixed p.p. distance from total
            const volumeColor = getValueColor(
              element.deltaVolume,
              totalElement.deltaVolume,
            );
            const avgPriceColor = getValueColor(
              element.deltaAvgPrice || 0,
              totalElement.deltaAvgPrice || 0,
            );
            const revenueColor = getValueColor(
              element.deltaRevenue,
              totalElement.deltaRevenue,
            );
            const marginPctColor = getValueColor(
              element.deltaMarginPct,
              totalElement.deltaMarginPct,
            );

            // Highlight styles (centralized in utils.js)
            const hl = getElementHighlightStyles(
              element,
              totalElement,
              highlightEnabled,
            );
            const volumeStyles = hl.volume;
            const avgPriceStyles = hl.avgPrice;
            const revenueStyles = hl.revenue;
            const marginStyles = hl.margin;
            const marginPctStyles = hl.marginPct;

            return (
              <div
                key={idx}
                className={`${classes.dimensionTableRow} ${
                  isTotal ? classes.dimensionTableRowTotal : ""
                }`}
              >
                <div className={classes.dimensionTableCell}>
                  <Tooltip title={element.name} placement="top-start">
                    <span className={classes.dimensionElementName}>
                      {element.name}
                    </span>
                  </Tooltip>
                </div>
                <div className={classes.dimensionTableCell}>
                  <span
                    className={classes.dimensionColoredValue}
                    style={{
                      color: volumeColor,
                      opacity: volumeStyles.opacity,
                      transform: `scale(${volumeStyles.scale})`,
                    }}
                  >
                    {formatPercentValue(element.deltaVolume)}
                  </span>
                </div>
                <div className={classes.dimensionTableCell}>
                  <span
                    className={classes.dimensionColoredValue}
                    style={{
                      color: avgPriceColor,
                      opacity: avgPriceStyles.opacity,
                      transform: `scale(${avgPriceStyles.scale})`,
                    }}
                  >
                    {formatPercentValue(element.deltaAvgPrice || 0)}
                  </span>
                </div>
                <div className={classes.dimensionTableCell}>
                  <span
                    className={classes.dimensionColoredValue}
                    style={{
                      color: revenueColor,
                      opacity: revenueStyles.opacity,
                      transform: `scale(${revenueStyles.scale})`,
                    }}
                  >
                    {formatPercentValue(element.deltaRevenue)}
                  </span>
                </div>
                <div className={classes.dimensionTableCell}>
                  {/* Delta Margin $ - no color coding, formatted with magnitude */}
                  <span
                    className={classes.dimensionColoredValue}
                    style={{
                      opacity: marginStyles.opacity,
                      transform: `scale(${marginStyles.scale})`,
                    }}
                  >
                    {formatMarginValue(element.deltaMargin)}
                  </span>
                </div>
                <div className={classes.dimensionTableCell}>
                  <span
                    className={classes.dimensionColoredValue}
                    style={{
                      color: marginPctColor,
                      opacity: marginPctStyles.opacity,
                      transform: `scale(${marginPctStyles.scale})`,
                    }}
                  >
                    {formatPercentValue(element.deltaMarginPct)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Stacked Bar Chart - Revenue Representation */}
      <div className={classes.dimensionChartSection}>
        <div className={classes.dimensionChartTitle}>
          {t("alignment_session.by_dimension.revenue_share")}
        </div>
        <div className={classes.dimensionChartWrapper}>
          {chartData && <Bar data={chartData} options={chartOptions} />}
        </div>
        {/* Chart Legend */}
        <div className={classes.dimensionChartLegend}>
          {data.elements.slice(0, 8).map((element, idx) => (
            <div key={idx} className={classes.dimensionChartLegendItem}>
              <span
                className={classes.dimensionChartLegendColor}
                style={{
                  backgroundColor: chartColors[idx % chartColors.length],
                }}
              />
              <span className={classes.dimensionChartLegendLabel}>
                {element.name}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

DimensionTable.propTypes = {
  dimensionKey: PropTypes.string.isRequired,
  dimensionData: PropTypes.object.isRequired,
  totalData: PropTypes.object.isRequired,
  isTotal: PropTypes.bool,
  onDimensionChange: PropTypes.func,
  highlightEnabled: PropTypes.bool,
  dimensionOptions: PropTypes.array,
};

export default DimensionTable;
